import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";

import Home from "@/views/Home.vue";
import UserLogin from "@/views/auth/UserLogin.vue";
import Meetings from "@/views/meeting/Meetings.vue";
import MeetingDetails from "@/views/meeting/MeetingDetails.vue";
import PricingPlans from "@/views/pricing-plan/PricingPlans.vue";
import ForgotPassword from "@/views/auth/ForgotPassword.vue";
import ResetPassword from "@/views/auth/ResetPassword.vue";
import SignUp from "@/views/auth/SignUp.vue";

import authGuard from "@/router/authNavigationGuard";
import redirectIfLoggedInGuard from "@/router/redirectIfLoggedInGuard";
import CustomerList from "@/views/customer/CustomerList.vue";
import CreateNewCustomer from "@/views/customer/CreateNewCustomer.vue";
import CustomerDetails from "@/views/customer/details/CustomerDetails.vue";
import SaleAccounts from "@/views/sale-account/SaleAccounts.vue";
import CustomerDetailsSubscription from "@/views/customer/details/CustomerDetailsSubscription.vue";
import CustomerDetailsOverview from "@/views/customer/details/CustomerDetailsOverview.vue";
import CustomerDetailsPaymentHistory from "@/views/customer/details/CustomerDetailsPaymentHistory.vue";
import CustomerTokens from "@/views/customer/details/CustomerTokens.vue";
import AdminAccounts from "@/views/admin-accounts/AdminAccounts.vue";
import SubscriptionPlans from "@/views/subscription-plans/SubscriptionPlans.vue";
import NewSubscriptionPlan from "@/views/subscription-plans/NewSubscriptionPlan.vue";
import EditSubscriptionPlan from "@/views/subscription-plans/EditSubscriptionPlan.vue";
import EditRegion from "@/views/location/EditRegion.vue";
import EditCustomer from "@/views/customer/EditCustomer.vue";
import ContactList from "@/views/contact/ContactList.vue";
import NewMeeting from "@/views/meeting/NewMeeting.vue";
import EditMeeting from "@/views/meeting/EditMeeting.vue";
import ManageAccount from "@/views/account/ManageAccount.vue";
import JoinMeeting from "@/views/meeting/JoinMeeting.vue";
import JoinMeetingForm from "@/views/meeting/JoinMeetingForm.vue";
import NotFound from "@/views/NotFound.vue";
import CustomerUserAccounts from "@/views/customer/CustomerUserAccounts.vue";
import EmailTemplates from "@/views/EmailTemplates.vue";
import AppNames from "@/views/app-names/AppNames.vue";
import Dashboard from "@/views/Dashboard.vue";
import CustomerMeetings from "@/views/customer/details/CustomerMeetings.vue";
import SmartVoiceTokens from "@/views/customer/details/SmartVoiceTokens.vue";
import MeetingCount from "@/views/reports/MeetingCount.vue";
import MeetingCount2 from "@/views/reports/MeetingCount2.vue";
import MeetingCountTotal from "@/views/reports/MeetingCountTotal.vue";
import ErrorStat from "@/views/reports/ErrorStat.vue";
import NewUsersDay from "@/views/reports/NewUsersDay.vue";
import NewContractsDay from "@/views/reports/NewContractsDay.vue";
import MeetingDetailReport from "@/views/reports/MeetingDetailReport.vue";
import CalendarList from "@/views/calendar/CalendarList.vue";
import NewCalendar from "@/views/calendar/NewCalendar.vue";
import LocationList from "@/views/locations/LocationList.vue";
import EditCalendar from "@/views/calendar/EditCalendar.vue";
import DefaultAccount from "@/views/admin-settings/DefaultAccount.vue";
import PasswordPolicy from "@/views/admin-settings/PasswordPolicy.vue";
import ChangePassword from "@/views/auth/ChangePassword.vue";
import CustomerAdminAccounts from "@/views/customer-admin-accounts/CustomerAdminAccounts.vue";
import Location from "@/views/location/RegionManagement.vue";
import SystemLogs from "@/views/system-logs/SystemLogs.vue";
import IpPolicy from "@/views/admin-settings/IpPolicy.vue";
import MonitorMeeting from "@/views/monitor/MonitorMeeting.vue";
import BackupPolicy from "@/views/admin-settings/BackupPolicy.vue";
import PortPolicy from "@/views/admin-settings/PortPolicy.vue";
import ManagerConfig from "@/views/admin-settings/ManageConfig.vue";
import EditApiConfig from "@/views/api-config/EditApiConfig.vue";
import NewApiConfig from "@/views/api-config/NewApiConfig.vue";
import ConfigList from "@/views/manage-config/ConfigList.vue";
import Activation from "@/views/auth/Activation.vue";
// import Widget from '@/views/widget/widget.vue';
import GuideAPI from "@/views/guide-api/guide-api.vue";
import SmeOss from "@/views/auth/SmeOss.vue";
import ManagerConfigToken from "@/views/admin-settings/ManageConfigToken.vue";
import NewRegion from "@/views/location/NewRegion.vue";
import OutputStatistic from "@/views/reports/OutputStatistic.vue";
import ManagePrivacy from "@/views/manage-privacy/ManagePrivacy.vue";
import CreatePrivacy from "@/views/manage-privacy/CreatePrivacy.vue";
import { MeetingMode } from "@/views/enums/MeetingMode";

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  { path: "/", name: "Home", component: Home },
  { path: "/login", name: "Login", component: UserLogin, beforeEnter: redirectIfLoggedInGuard },
  { path: "/signup", name: "SignUp", component: SignUp, beforeEnter: redirectIfLoggedInGuard },
  { path: "/activation", name: "Activation", component: Activation, beforeEnter: redirectIfLoggedInGuard },
  { path: "/change-password", name: "ChangePassword", component: ChangePassword },
  { path: "/forgot-password", name: "ForgotPassword", component: ForgotPassword, beforeEnter: redirectIfLoggedInGuard },
  {
    path: "/reset-password/:secretKey",
    name: "ResetPassword",
    component: ResetPassword,
    beforeEnter: redirectIfLoggedInGuard,
  },

  { path: "/dashboard", name: "Dashboard", component: Dashboard, beforeEnter: authGuard },

  { path: "/reports/meeting-count", name: "MeetingCount", component: MeetingCount, beforeEnter: authGuard },
  { path: "/reports/meeting-count-d", name: "MeetingCount2", component: MeetingCount2, beforeEnter: authGuard },
    { path: "/reports/output-statistic", name: "OutputStatistic", component: OutputStatistic, beforeEnter: authGuard },
  {
    path: "/reports/meeting-count-total",
    name: "MeetingCountTotal",
    component: MeetingCountTotal,
    beforeEnter: authGuard,
  },
  { path: "/reports/error-stat", name: "ErrorStat", component: ErrorStat, beforeEnter: authGuard },
  { path: "/reports/new-users", name: "NewUsersDay", component: NewUsersDay, beforeEnter: authGuard },
  { path: "/reports/new-contracts", name: "NewContractsDay", component: NewContractsDay, beforeEnter: authGuard },
  {
    path: "/reports/meeting-detail-report",
    name: "MeetingDetailReport",
    component: MeetingDetailReport,
    beforeEnter: authGuard,
  },

  { path: "/meetings/new", name: "NewMeeting", component: NewMeeting, beforeEnter: authGuard },
  { path: "/meetings/details/:id", name: "MeetingDetails", component: MeetingDetails, beforeEnter: authGuard },
  { path: "/meetings/edit/:id", name: "EditMeeting", component: EditMeeting, meta: { mode: MeetingMode.EDIT }, beforeEnter: authGuard },
  { path: "/meetings/clone/:id", name: "CloneMeeting", component: EditMeeting, meta: { mode: MeetingMode.CLONE }, beforeEnter: authGuard },
  { path: "/meetings", name: "Meetings", component: Meetings, beforeEnter: authGuard },

  {
    path: "/join-meeting/:meetingSecretKey",
    name: "JoinMeeting",
    component: JoinMeeting,
    meta: { mayHaveOrgField: true },
  },
  { path: "/host/:meetingSecretKey", name: "HostMeeting", component: JoinMeeting },
  {
    path: "/join/:meetingSecretKey",
    name: "JoinMeetingFormWithKey",
    component: JoinMeetingForm,
    meta: { mayHaveOrgField: true },
  },
  {
    path: "/gm",
    name: "JoinMeetingForm",
    component: JoinMeetingForm,
    meta: { mayHaveOrgField: true },
  },

  { path: "/pricing-plans", name: "PricingPlans", component: PricingPlans, beforeEnter: authGuard },
  { path: "/customers/create-new", name: "CreateNewCustomer", component: CreateNewCustomer, beforeEnter: authGuard },
  {
    path: "/customers/details/:id",
    component: CustomerDetails,
    beforeEnter: authGuard,
    children: [
      { path: "", name: "CustomerDetailsOverview", component: CustomerDetailsOverview },
      { path: "subscription", name: "CustomerDetailsSubscription", component: CustomerDetailsSubscription },
      { path: "meetings", name: "CustomerMeetings", component: CustomerMeetings },
      { path: "payment-history", name: "CustomerDetailsPaymentHistory", component: CustomerDetailsPaymentHistory },
      { path: "tokens", name: "CustomerTokens", component: CustomerTokens },
      { path: "smartvoicetokens", name: "SmartVoiceTokens", component: SmartVoiceTokens },
    ],
  },
  { path: "/customers/edit/:id", name: "EditCustomer", component: EditCustomer, beforeEnter: authGuard },
  { path: "/customers", name: "CustomerList", component: CustomerList, beforeEnter: authGuard },

  { path: "/sale-accounts", name: "SaleAccounts", component: SaleAccounts, beforeEnter: authGuard },
  { path: "/admin-accounts", name: "AdminAccounts", component: AdminAccounts, beforeEnter: authGuard },
  { path: '/customer-admin-accounts', name: 'CustomerAdminAccounts', component: CustomerAdminAccounts, beforeEnter: authGuard },
  //
  { path: "/location", name: "RegionManagement", component: Location, beforeEnter: authGuard },
  { path: "/location/new", name: "NewRegion", component: NewRegion, beforeEnter: authGuard },
  { path: "/location/edit/:id", name: "EditRegion", component: EditRegion, beforeEnter: authGuard },
  {
    path: "/customer-user-accounts",
    name: "CustomerUserAccounts",
    component: CustomerUserAccounts,
    beforeEnter: authGuard,
  },

  {
    path: "/subscription-plans/edit/:id",
    name: "SubscriptionPlanDetails",
    component: EditSubscriptionPlan,
    beforeEnter: authGuard,
  },
  {
    path: "/subscription-plans/new",
    name: "NewSubscriptionPlan",
    component: NewSubscriptionPlan,
    beforeEnter: authGuard,
  },
  { path: "/subscription-plans", name: "SubscriptionPlans", component: SubscriptionPlans, beforeEnter: authGuard },

  { path: "/contacts", name: "ContactList", component: ContactList, beforeEnter: authGuard },
  { path: "/email-templates", name: "EmailTemplates", component: EmailTemplates, beforeEnter: authGuard },

  { path: "/calendar/new", name: "NewCalendar", component: NewCalendar, beforeEnter: authGuard },
  { path: "/calendar/edit", name: "EditCalendar", component: EditCalendar, beforeEnter: authGuard },
  { path: "/calendar", name: "CalendarList", component: CalendarList, beforeEnter: authGuard },

  { path: "/locations", name: "LocationList", component: LocationList, beforeEnter: authGuard },

  { path: "/app-names", name: "AppNames", component: AppNames, beforeEnter: authGuard },

  {
    path: "/admin-settings/default-account",
    name: "DefaultAccount",
    component: DefaultAccount,
    beforeEnter: authGuard,
  },
  {
    path: "/admin-settings/password-policy",
    name: "PasswordPolicy",
    component: PasswordPolicy,
    beforeEnter: authGuard,
  },
  { path: "/admin-settings/ip-policy", name: "IpPolicy", component: IpPolicy, beforeEnter: authGuard },
  { path: "/system-logs", name: "SystemLogs", component: SystemLogs, beforeEnter: authGuard },
  { path: "/monitor-meeting/:id", name: "MonitorMeeting", component: MonitorMeeting, beforeEnter: authGuard },
  { path: "/admin-settings/backup-policy", name: "BackupPolicy", component: BackupPolicy, beforeEnter: authGuard },
  { path: "/admin-settings/port-policy", name: "PortPolicy", component: PortPolicy, beforeEnter: authGuard },
  { path: "/admin-settings/manage-config", name: "ManagerConfig", component: ManagerConfig, beforeEnter: authGuard },
  {
    path: "/admin-settings/manage-config-token",
    name: "ManagerConfigToken",
    component: ManagerConfigToken,
    beforeEnter: authGuard,
  },
  {
    path: "/admin-settings/api-config/edit/:id",
    name: "EditApiConfig",
    component: EditApiConfig,
    beforeEnter: authGuard,
  },
  { path: "/admin-settings/api-config/new", name: "NewApiConfig", component: NewApiConfig, beforeEnter: authGuard },
  { path: "/manage-config/config-list", name: "ConfigList", component: ConfigList, beforeEnter: authGuard },
  { path: "/whiteboard", name: "Whiteboard", component: () => import('@/views/whiteboard/WhiteboardList.vue'), beforeEnter: authGuard },
  { path: "/manage-privacy", name: "ManagePrivacy", component: ManagePrivacy, beforeEnter: authGuard },
  {
    path: "/manage-privacy/create",
    name: "CreatePrivacy",
    component: () => import("@/views/manage-privacy/CreatePrivacy.vue"),
    beforeEnter: authGuard,
  },
  {
    path: "/manage-privacy/update/:id",
    name: "UpdatePrivacy",
    component: () => import("@/views/manage-privacy/UpdatePrivacy.vue"),
    beforeEnter: authGuard,
  },



  { path: "/account", name: "ManageAccount", component: ManageAccount },
  // { path: "/widget", name: "widget", component: Widget },
  { path: "/guideapi", name: "GuideAPI", component: GuideAPI },
  { path: "/sso/onesme", name: "SmeOss", component: SmeOss },
  // { path: '/place-order/payment', name: 'PlaceOrderPayment', component: PlaceOrderPayment, beforeEnter: authGuard },
  // { path: '/place-order/create-account', name: 'PlaceOrderCreateAccount', component: PlaceOrderCreateAccount },
  // { path: '/place-order/successful', name: 'OrderSuccessful', component: OrderSuccessful, beforeEnter: authGuard }
  { path: "*", name: "404", component: NotFound },
];

const router = new VueRouter({
  routes,
  linkActiveClass: "active",
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

export default router;
